(() => {
  if (window.__kidsCampaignInitialized) return;
  window.__kidsCampaignInitialized = true;

  const submittedFormStorageKey = 'kidsCampaignSubmittedForm';
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, value));

  class KidsCampaignTimer extends HTMLElement {
    connectedCallback() {
      this.startTime = Date.parse(this.dataset.start || '');
      this.endTime = Date.parse(this.dataset.end || '');
      this.progressElement = this.querySelector('[data-campaign-progress]');
      this.milestoneElements = Array.from(this.querySelectorAll('[data-campaign-milestone]'));
      this.progressTarget = 0;
      this.currentProgress = 0;
      this.hasStartedProgressAnimation = false;

      if (!Number.isFinite(this.endTime)) {
        this.classList.remove('kids-campaign-timer--awaiting');
        this.applyProgress(0);
        return;
      }

      this.update();
      this.observeEntrance();
      if (Date.now() < this.endTime) {
        this.interval = window.setInterval(() => this.update(), 1000);
      }
    }

    disconnectedCallback() {
      if (this.interval) window.clearInterval(this.interval);
      if (this.enterAnimationFrame) window.cancelAnimationFrame(this.enterAnimationFrame);
      if (this.progressAnimationFrame) window.cancelAnimationFrame(this.progressAnimationFrame);
      this.entranceObserver?.disconnect();
    }

    observeEntrance() {
      const startAnimation = () => {
        if (this.hasStartedProgressAnimation) return;
        this.hasStartedProgressAnimation = true;

        if (!reduceMotionQuery.matches) {
          this.classList.add('kids-campaign-timer--entering');
          this.classList.remove('kids-campaign-timer--awaiting');
          this.enterAnimationFrame = window.requestAnimationFrame(() => {
            this.enterAnimationFrame = window.requestAnimationFrame(() => {
              this.classList.remove('kids-campaign-timer--entering');
            });
          });
        } else {
          this.classList.remove('kids-campaign-timer--awaiting');
        }

        this.update(true);
      };

      if (!('IntersectionObserver' in window) || reduceMotionQuery.matches) {
        startAnimation();
        return;
      }

      this.entranceObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.entranceObserver?.disconnect();
        this.entranceObserver = undefined;
        startAnimation();
      }, { threshold: 0.2 });
      this.entranceObserver.observe(this);
    }

    update(animateProgress = false) {
      const now = Date.now();
      const hasEnded = now >= this.endTime;
      const progress = this.getProgress(now);

      this.updateMilestones(now, progress);
      if (this.hasStartedProgressAnimation || animateProgress) {
        this.updateProgress(progress, animateProgress);
      } else {
        this.progressTarget = progress;
      }

      if (hasEnded && this.interval) {
        window.clearInterval(this.interval);
        this.interval = undefined;
      }
    }

    getProgress(now) {
      const defaultProgress = Number.isFinite(this.startTime) && this.endTime > this.startTime
        ? clamp(((now - this.startTime) / (this.endTime - this.startTime)) * 100, 0, 100)
        : 0;
      const milestoneTimes = this.milestoneElements.map((milestone) => Date.parse(milestone.dataset.time || ''));

      if (
        milestoneTimes.length !== 4 ||
        !Number.isFinite(this.startTime) ||
        milestoneTimes.some((time) => !Number.isFinite(time)) ||
        milestoneTimes.some((time, index) => time <= (index === 0 ? this.startTime : milestoneTimes[index - 1]))
      ) {
        return defaultProgress;
      }

      const points = [this.startTime, ...milestoneTimes];
      const visualProgress = [0, 25, 50, 75, 100];

      if (now <= points[0]) return 0;
      if (now >= points[points.length - 1]) return 100;

      for (let index = 1; index < points.length; index += 1) {
        if (now > points[index]) continue;

        const segmentProgress = (now - points[index - 1]) / (points[index] - points[index - 1]);
        return visualProgress[index - 1] + (visualProgress[index] - visualProgress[index - 1]) * segmentProgress;
      }

      return defaultProgress;
    }

    updateMilestones(now, progress) {
      const completionStates = this.milestoneElements.map((milestone, index) => {
        const milestoneTime = Date.parse(milestone.dataset.time || '');
        return Number.isFinite(milestoneTime)
          ? now >= milestoneTime
          : progress >= (index + 1) * 25;
      });

      this.milestoneElements.forEach((milestone, index) => {
        const isComplete = completionStates[index];
        milestone.classList.toggle('is-complete', isComplete);
        milestone.classList.toggle(
          'is-current',
          isComplete && now < this.endTime && !completionStates[index + 1]
        );
      });
    }

    applyProgress(progress) {
      const progressValue = `${progress.toFixed(2)}%`;
      this.currentProgress = progress;
      this.style.setProperty('--campaign-progress', progressValue);
      if (this.progressElement) {
        this.progressElement.style.setProperty('--campaign-progress', progressValue);
        this.progressElement.setAttribute('aria-valuenow', String(Math.round(progress)));
      }
    }

    updateProgress(progress, animateProgress) {
      this.progressTarget = progress;

      if (this.progressAnimationFrame && !animateProgress) return;

      if (!animateProgress || reduceMotionQuery.matches || progress <= 0) {
        this.applyProgress(progress);
        return;
      }

      this.classList.add('kids-campaign-timer--progress-animating');
      const duration = 1600;
      const startTime = performance.now();

      const animate = (now) => {
        const elapsed = clamp((now - startTime) / duration, 0, 1);
        const eased = 1 - Math.pow(1 - elapsed, 3);
        this.applyProgress(progress * eased);

        if (elapsed < 1) {
          this.progressAnimationFrame = window.requestAnimationFrame(animate);
          return;
        }

        this.progressAnimationFrame = undefined;
        this.classList.remove('kids-campaign-timer--progress-animating');
        this.applyProgress(this.progressTarget);
      };

      this.progressAnimationFrame = window.requestAnimationFrame(animate);
    }
  }

  if (!customElements.get('kids-campaign-timer')) {
    customElements.define('kids-campaign-timer', KidsCampaignTimer);
  }

  class KidsCampaignIntroScrollAnimation {
    constructor() {
      this.contents = new Set();
      this.frame = undefined;
      this.isListening = false;
      this.update = this.update.bind(this);
      this.requestUpdate = this.requestUpdate.bind(this);
    }

    connect(scope = document) {
      if (reduceMotionQuery.matches) return;

      scope.querySelectorAll('[data-campaign-intro-content]').forEach((content) => {
        this.contents.add(content);
      });

      if (!this.contents.size) return;

      this.update();
      if (!this.isListening) {
        window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.requestUpdate);
        this.isListening = true;
      }
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    }

    update() {
      this.frame = undefined;
      const viewportHeight = window.innerHeight;

      this.contents.forEach((content) => {
        if (!content.isConnected) {
          this.contents.delete(content);
          return;
        }

        const scrollContainer = content.closest(
          '.kids-campaign-intro__hero, .kids-campaign-reward'
        );
        if (!scrollContainer) return;

        const rect = scrollContainer.getBoundingClientRect();
        const scrollProgress = clamp(
          (viewportHeight - rect.top) / (viewportHeight + rect.height),
          0,
          1
        );
        const enterProgress = clamp(scrollProgress / 0.35, 0, 1);
        const exitProgress = clamp((scrollProgress - 0.62) / 0.38, 0, 1);
        const opacity = Math.min(enterProgress, 1 - exitProgress);
        const translateY = 96 * (1 - enterProgress) - 72 * exitProgress;
        const scale = 0.5 + 0.5 * enterProgress + 0.5 * exitProgress;

        content.style.setProperty('--intro-content-opacity', opacity.toFixed(3));
        content.style.setProperty('--intro-content-translate-y', `${translateY.toFixed(2)}px`);
        content.style.setProperty('--intro-content-scale', scale.toFixed(3));
        content.classList.add('is-scroll-animated');
      });
    }
  }

  const introScrollAnimation = new KidsCampaignIntroScrollAnimation();
  introScrollAnimation.connect();

  class KidsCampaignFlyScrollAnimation {
    constructor() {
      this.items = new Set();
      this.frame = undefined;
      this.isListening = false;
      this.update = this.update.bind(this);
      this.requestUpdate = this.requestUpdate.bind(this);
    }

    connect(scope = document) {
      if (reduceMotionQuery.matches) return;

      scope.querySelectorAll('[data-campaign-fly]').forEach((item) => {
        this.items.add(item);
      });

      if (!this.items.size) return;

      this.update();
      if (!this.isListening) {
        window.addEventListener('scroll', this.requestUpdate, { passive: true });
        window.addEventListener('resize', this.requestUpdate);
        this.isListening = true;
      }
    }

    requestUpdate() {
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(this.update);
    }

    update() {
      this.frame = undefined;
      const viewportHeight = window.innerHeight;

      this.items.forEach((item) => {
        if (!item.isConnected) {
          this.items.delete(item);
          return;
        }

        const section = item.closest('.kids-campaign-reward');
        if (!section) return;

        const rect = section.getBoundingClientRect();
        const scrollProgress = clamp(
          (viewportHeight - rect.top) / (viewportHeight + rect.height),
          0,
          1
        );
        const enterProgress = clamp(scrollProgress / 0.3, 0, 1);
        const exitProgress = clamp((scrollProgress - 0.7) / 0.3, 0, 1);
        const opacity = Math.min(enterProgress, 1 - exitProgress);
        const translateX = -180 * (1 - enterProgress) + 240 * exitProgress;

        item.style.setProperty('--campaign-fly-opacity', opacity.toFixed(3));
        item.style.setProperty('--campaign-fly-translate-x', `${translateX.toFixed(2)}px`);
        item.classList.add('is-scroll-fly-animated');
      });
    }
  }

  const campaignFlyScrollAnimation = new KidsCampaignFlyScrollAnimation();
  campaignFlyScrollAnimation.connect();

  const rememberSubmittedForm = (event) => {
    const form = event.target.closest('form.kids-campaign-signup__form');
    if (!form?.id) return;

    try {
      window.sessionStorage.setItem(submittedFormStorageKey, form.id);
    } catch (_error) {
      // 浏览器禁用会话存储时仍由 return_to 哈希定位提交表单。
    }
  };

  const copyCampaignDiscountCode = async (button) => {
    const discountCode = button.dataset.campaignDiscountCode;
    const label = button.querySelector('[data-campaign-copy-label]');
    const status = button.parentElement?.querySelector('[data-campaign-copy-status]');
    if (!discountCode || !label) return;

    const defaultLabel = label.dataset.defaultLabel || label.textContent;
    label.dataset.defaultLabel = defaultLabel;

    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(discountCode);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = discountCode;
        textArea.setAttribute('readonly', '');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.append(textArea);
        textArea.select();
        const isCopied = document.execCommand('copy');
        textArea.remove();
        if (!isCopied) throw new Error('Clipboard copy failed');
      }

      window.clearTimeout(button.copyResetTimer);
      button.classList.add('is-copied');
      label.textContent = 'Copied';
      if (status) status.textContent = `Discount code ${discountCode} copied.`;
      button.copyResetTimer = window.setTimeout(() => {
        button.classList.remove('is-copied');
        label.textContent = defaultLabel;
        if (status) status.textContent = '';
      }, 1800);
    } catch (_error) {
      if (status) status.textContent = 'Unable to copy. Please copy the code manually.';
    }
  };

  const clearSubmittedFormState = (formId) => {
    document.querySelectorAll('[data-campaign-form-success]').forEach((marker) => marker.remove());

    try {
      window.sessionStorage.removeItem(submittedFormStorageKey);
    } catch (_error) {
      // 无需处理，会话存储不可用时不影响反馈展示。
    }

    if (window.location.hash.slice(1) !== formId || !window.history.replaceState) return;

    window.history.replaceState(
      window.history.state,
      document.title,
      `${window.location.pathname}${window.location.search}`
    );
  };

  const revealSubmittedFormResponse = (scope = document) => {
    let submittedFormId = '';
    const hash = window.location.hash.slice(1);

    if (hash.startsWith('KidsCampaignSignup-')) {
      submittedFormId = decodeURIComponent(hash);
    }

    if (!submittedFormId) {
      try {
        submittedFormId = window.sessionStorage.getItem(submittedFormStorageKey) || '';
      } catch (_error) {
        submittedFormId = '';
      }
    }

    if (!submittedFormId) {
      submittedFormId = scope.querySelector('[data-campaign-form-success]')?.closest('form')?.id || '';
    }

    scope.querySelectorAll('[data-campaign-signup]').forEach((signup) => {
      signup.classList.remove('has-server-response');
    });

    if (!submittedFormId) return;

    const submittedForm = document.getElementById(submittedFormId);
    const signup = submittedForm?.closest('[data-campaign-signup]');
    if (!signup) return;

    const isSuccess = Boolean(submittedForm.querySelector('[data-campaign-form-success]'));
    if (!isSuccess) {
      const errorNotification = signup.querySelector('.kids-campaign-signup__notification--error');
      if (!errorNotification) return;

      signup.classList.add('has-server-response');
      errorNotification.focus({ preventScroll: true });
      clearSubmittedFormState(submittedFormId);
      return;
    }

    const successModal = document.getElementById(signup.dataset.campaignSuccessModalId || '');
    if (!successModal || typeof successModal.show !== 'function') return;

    signup.classList.add('has-server-response');
    successModal.show(submittedForm.querySelector('[type="submit"]'));
    window.requestAnimationFrame(() => {
      successModal.querySelector('[data-campaign-success-notification]')?.focus();
    });
    clearSubmittedFormState(submittedFormId);
  };

  document.addEventListener('submit', rememberSubmittedForm);

  document.addEventListener('click', (event) => {
    const signupSubmitButton = event.target.closest(
      '.kids-campaign-signup__form button[type="submit"]'
    );
    if (signupSubmitButton && typeof window.gtag === 'function') {
      const signupForm = signupSubmitButton.closest('.kids-campaign-signup__form');
      const section = signupSubmitButton.closest('[id^="shopify-section-"]');

      window.gtag('event', 'click_kids_campaign_signup', {
        form_id: signupForm?.id || '',
        form_name: 'kids_campaign_signup',
        section_id: section?.id.replace('shopify-section-', '') || '',
        button_label: signupSubmitButton.textContent.trim()
      });
    }

    const copyButton = event.target.closest('[data-campaign-copy-code]');
    if (copyButton) {
      event.preventDefault();
      copyCampaignDiscountCode(copyButton);
      return;
    }

    const takePartLink = event.target.closest(
      '.template-page-kids-back-to-school a[href="#campaign-signup"]'
    );

    if (!takePartLink || !window.matchMedia('(max-width: 749px)').matches) return;

    const rewards = document.getElementById('campaign-rewards');
    if (!rewards) return;

    event.preventDefault();
    rewards.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start'
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    revealSubmittedFormResponse(event.target);
    introScrollAnimation.connect(event.target);
    campaignFlyScrollAnimation.connect(event.target);
  });
  window.addEventListener('pageshow', () => revealSubmittedFormResponse());
  revealSubmittedFormResponse();
})();
